// Authenticated self-service enrollment completion — SPA client
// (Auth0-first mode, Task 3). Mirrors the route surface in
// apps/api/src/routes/enrollMe.ts, mounted at `/api/enroll/me` for the
// LOGGED-IN coworker (resolved server-side via authSub). Unlike
// api/enroll.ts (public, no-auth, contractor OTP path), every call here
// carries the caller's Auth0 access token — see `apiRequest`'s `token`
// option — and there is no OTP start/verify/resend: email verification is
// already asserted by Auth0 (see `emailVerified` gate in enrollMe.ts).
import { apiRequest } from './client'
import type { Coworker, EnrollmentStatus } from '@hub-wifi/shared'

/** Mirrors `MyEnrollmentCompleteness` in
 * apps/api/src/services/enrollmentService.ts — `status` is `'not-started'`
 * when the caller has never begun the authenticated flow (no
 * `CoworkerEnrollment` row yet), otherwise the in-flight row's real
 * `EnrollmentStatus`. `missing` lists which of the completion fields still
 * need work. */
export interface MyEnrollmentCompleteness {
  status: EnrollmentStatus | 'not-started'
  missing: Array<'mobilePhone' | 'taxIdValue' | 'facePhoto'>
}

/** PATCH body for `/api/enroll/me/profile` — the whitelisted subset of
 * `updateCoworkerProfileSchema` this endpoint accepts (see
 * `enrollmentService.ts#updateMyProfile`'s `Pick<...>`). Every field is
 * optional (PATCH semantics). */
export interface MyEnrollProfilePatch {
  mobilePhone?: string
  taxIdCountry?: string
  taxIdType?: string
  taxIdValue?: string
  birthDate?: string
}

/** Body for `/api/enroll/me/face` — mirrors the public
 * `submitEnrollmentFace` payload minus `stepToken` (the authenticated path
 * resolves the in-flight enrollment via the caller's own coworker id
 * instead). */
export interface MyEnrollFacePayload {
  photoBase64: string
  consent: boolean
}

/** GET /api/enroll/me — completeness report for the logged-in coworker.
 * 403s `EMAIL_NOT_VERIFIED` if the Auth0 email isn't verified; 404s if the
 * caller has no linked coworker record (both surface as `ApiError`). */
export function getMyEnrollment(token: string): Promise<MyEnrollmentCompleteness> {
  return apiRequest<MyEnrollmentCompleteness>('/api/enroll/me', { token })
}

/** PATCH /api/enroll/me/profile — persist the whitelisted enrollment-
 * completion fields for the caller's own coworker record. Returns the
 * updated `Coworker`. */
export function putMyEnrollProfile(patch: MyEnrollProfilePatch, token: string): Promise<Coworker> {
  return apiRequest<Coworker>('/api/enroll/me/profile', {
    method: 'PATCH',
    token,
    body: patch,
  })
}

/** POST /api/enroll/me/face — stage the consented selfie against the
 * caller's own in-flight enrollment row. Does NOT decide approval — that's
 * a separate `finalizeMyEnroll` call. */
export function postMyEnrollFace(
  payload: MyEnrollFacePayload,
  token: string,
): Promise<{ status: EnrollmentStatus }> {
  return apiRequest<{ status: EnrollmentStatus }>('/api/enroll/me/face', {
    method: 'POST',
    token,
    body: payload,
  })
}

/** POST /api/enroll/me/finalize — decide approval for the already-staged
 * photo (requires a prior `postMyEnrollFace` call). Response `status` is
 * either `'approved'` or `'pending-approval'`. */
export function finalizeMyEnroll(token: string): Promise<{ status: EnrollmentStatus }> {
  return apiRequest<{ status: EnrollmentStatus }>('/api/enroll/me/finalize', {
    method: 'POST',
    token,
  })
}
